import { useIsFetching } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Joyride } from 'react-joyride'
import type {
  EventData,
  Step,
  TourData,
  TooltipRenderProps,
} from 'react-joyride'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import { localStorageItemKeys } from '~constants/local-storage-items'
import { useNavigation } from '~components/navigation-and-footer/navigation/navigation-context'
import {
  GuidedTour as GuidedTourI,
  IUserInfoReadResponse,
} from '~hooks/queries/me/me-types'
import { useGetMe } from '~hooks/queries/me/use-get-me'
import { usePatchMeVT } from '~hooks/queries/me/use-patch-me-vt'
import useBreakpoint from '~hooks/use-breakpoint'
import { COLORS } from '~theme/colors'
import { Z_INDEX } from '~types/z-index'
import { GuidedTourListItem } from './content/guided-tours-list'
import { filterAvailableSteps } from './hooks/filter-available-steps'
import { scrollTargetIntoHorizontalView } from './hooks/scroll-target-into-horizontal-view'
import { useSettledSteps } from './hooks/use-settled-steps'
import { useStickyScrollOffset } from './hooks/use-sticky-scroll-offset'
import { useTour } from './hooks/use-tour'
import { CustomTooltip } from './sub-components/custom-tooltip'

function getInitialStepIndex() {
  const stepIndex = localStorage.getItem(
    localStorageItemKeys.guidedTourInitialStepIndex,
  )

  return stepIndex ? Number(stepIndex) : undefined
}

function shouldAutoStartTour(tour: GuidedTourListItem, insertedAt?: string) {
  if (!tour.autoStartForUsersJoinedFrom) return true

  return (
    insertedAt !== undefined &&
    new Date(insertedAt) >= new Date(tour.autoStartForUsersJoinedFrom)
  )
}

export function GuidedTour() {
  const location = useLocation()
  const [startTour, setStartTour] = useState(false)
  const tour = useTour()
  const { data: me } = useGetMe()
  const isStable = useIsStable()
  const initialStepIndex = getInitialStepIndex()
  const { setTourLink, setTourVesselLink } = useNavigation()
  const vesselLink: unknown = tour?.content.find(
    (step) => step.data?.vesselLink,
  )?.data?.vesselLink

  useEffect(() => {
    const isActive =
      startTour ||
      initialStepIndex !== undefined ||
      localStorage.getItem(localStorageItemKeys.triggeredGuidedTour) ===
        tour?.id
    setTourVesselLink(
      isActive && typeof vesselLink === 'string' ? vesselLink : undefined,
    )
    return () => setTourVesselLink(undefined)
  }, [vesselLink, setTourVesselLink, startTour, initialStepIndex, tour?.id])
  const navigationLink: unknown =
    tour?.content[initialStepIndex ?? 0]?.data?.navigationLink

  useEffect(() => {
    if (typeof navigationLink !== 'string') return
    setTourLink(navigationLink)
    return () => setTourLink(undefined)
  }, [navigationLink, setTourLink])

  const tourKey = `${tour?.id ?? ''}:${location.pathname}`
  const settledSteps = useSettledSteps(
    tour?.content ?? [],
    tour?.isLoading ? `${tourKey}:loading` : tourKey,
    initialStepIndex,
  )
  const isShowingLoader = Boolean(tour?.isLoading && startTour)
  const [loaderTourKey, setLoaderTourKey] = useState<string>()

  useEffect(() => {
    if (isShowingLoader) setLoaderTourKey(tourKey)
  }, [isShowingLoader, tourKey])

  useEffect(() => {
    const isTourStartPath = !tour?.startTourPath
      ? false
      : matchPath({ path: tour?.startTourPath }, location.pathname)

    if (
      !tour ||
      (!isStable && !tour.isLoading) ||
      (!initialStepIndex && !isTourStartPath)
    ) {
      return
    }

    const isStartedByUser =
      Boolean(initialStepIndex) ||
      localStorage.getItem(localStorageItemKeys.triggeredGuidedTour) === tour.id

    if (!isStartedByUser && !shouldAutoStartTour(tour, me?.insertedAt)) {
      return
    }

    setStartTour(true)
  }, [location, tour, initialStepIndex, isStable, me?.insertedAt])

  if (!tour || !startTour) return null

  if (tour.isLoading) return <TourLoader steps={tour.content} />

  // The loader already waited for the tour's data, so its deferred steps can
  // start without waiting for page targets to settle.
  const steps =
    loaderTourKey === tourKey
      ? filterAvailableSteps(tour.content)
      : settledSteps

  return steps?.length ? (
    <TourRenderer
      key={tourKey}
      tour={tour}
      steps={steps}
      initialStepIndex={initialStepIndex}
    />
  ) : null
}

function TourLoader({ steps }: { steps: Step[] }) {
  const options = useMemo(
    () => ({
      primaryColor: COLORS.primary.maerskBlue,
      zIndex: Z_INDEX.Tour,
      overlayClickAction: false as const,
    }),
    [],
  )

  return (
    <Joyride
      steps={steps}
      tooltipComponent={CustomTooltip}
      options={options}
      run
    />
  )
}

function TourRenderer({
  tour,
  steps,
  initialStepIndex,
}: {
  tour: GuidedTourListItem
  steps: Step[]
  initialStepIndex: number | undefined
}) {
  const { data: me } = useGetMe()
  const { setTourLink, setTourVesselLink } = useNavigation()
  const [run, setRun] = useState(true)
  const [stepIndex, setStepIndex] = useState(initialStepIndex)
  const navigate = useNavigate()
  const location = useLocation()
  const { mutateAsync: patchMe } = usePatchMeVT()
  const { offset: scrollOffset, measure: measureStickyOffset } =
    useStickyScrollOffset()
  const isFinishedRef = useRef(false)
  const [tourSteps] = useState(steps)
  const [isTourVisible, setIsTourVisible] = useState(true)
  const { isMobileSize } = useBreakpoint()

  useLockPageScroll(Boolean(isMobileSize) && run && isTourVisible)

  const completeTour = useCallback(() => {
    if (isFinishedRef.current) return
    isFinishedRef.current = true
    if (tour.status !== 'Completed') {
      patchMe(
        constructPayload({ me, tour: { id: tour.id, status: 'Completed' } }),
      )
    }
    localStorage.removeItem(localStorageItemKeys.guidedTourInitialStepIndex)
  }, [me, tour, patchMe])

  const renderTooltip = useCallback(
    (props: TooltipRenderProps) => (
      <CustomTooltip
        {...props}
        onStartTour={(id, link) => {
          completeTour()
          setTourLink(undefined)
          setTourVesselLink(undefined)
          setRun(false)
          localStorage.setItem(localStorageItemKeys.triggeredGuidedTour, id)
          navigate(link)
        }}
      />
    ),
    [completeTour, navigate, setTourLink, setTourVesselLink],
  )

  const joyrideOptions = useMemo(
    () => ({
      primaryColor: COLORS.primary.maerskBlue,
      zIndex: Z_INDEX.Tour,
      overlayClickAction: false as const,
      scrollOffset,
      before: async ({ step }: TourData) => {
        measureStickyOffset()
        scrollTargetIntoHorizontalView(step.target)
        // Let the re-measured offset commit before the step is positioned.
        await new Promise(requestAnimationFrame)
      },
    }),
    [scrollOffset, measureStickyOffset],
  )

  function handleCallback(data: EventData) {
    const {
      action,
      controlled,
      step: { data: { next = null, previous = null } = {} } = {},
      type,
      index,
    } = data

    const isLastStep = index === tourSteps.length - 1

    if (
      isLastStep &&
      (action === 'close' ||
        (type === 'tour:end' && data.status === 'finished')) &&
      !isFinishedRef.current
    ) {
      completeTour()
    }

    if (action === 'update' && !isFinishedRef.current) {
      if (!localStorage.getItem(localStorageItemKeys.triggeredGuidedTour))
        localStorage.setItem(localStorageItemKeys.triggeredGuidedTour, tour.id)
    }

    if (type === 'tour:end') setIsTourVisible(false)

    if (action === 'close' || action === 'skip') {
      setIsTourVisible(false)
      setTourLink(undefined)
      setTourVesselLink(undefined)
      isFinishedRef.current = true
      localStorage.removeItem(localStorageItemKeys.guidedTourInitialStepIndex)
      localStorage.removeItem(localStorageItemKeys.triggeredGuidedTour)

      if (action === 'close' && isLastStep) {
        setRun(false)
      }

      if (
        action === 'skip' &&
        tour.status !== 'Skipped' &&
        tour.status !== 'Completed'
      ) {
        patchMe(
          constructPayload({ me, tour: { id: tour.id, status: 'Skipped' } }),
        )
      }
      return
    }

    if (type === 'step:after') {
      if (
        index === 0 &&
        tour.status !== 'Started' &&
        tour.status !== 'Completed'
      ) {
        patchMe(
          constructPayload({ me, tour: { id: tour.id, status: 'Started' } }),
        )
      }

      if (action === 'next') moveStep(1)
      if (action === 'prev') moveStep(-1)
    }

    if (type === 'error:target_not_found') {
      const arrivedBackwards =
        (location.state as { tourDirection?: number } | null)?.tourDirection ===
        -1
      const direction =
        action === 'prev' || (action !== 'next' && arrivedBackwards) ? -1 : 1
      if (tourSteps[index + direction]) moveStep(direction)
    }

    function moveStep(direction: 1 | -1) {
      const destination = direction === 1 ? next : previous
      const targetIndex = index + direction

      if (!destination) {
        const link: unknown = tourSteps[targetIndex]?.data?.navigationLink
        setTourLink(typeof link === 'string' ? link : undefined)
      }
      if (controlled && initialStepIndex !== undefined) {
        setStepIndex(targetIndex)
      }
      if (destination) {
        localStorage.setItem(localStorageItemKeys.triggeredGuidedTour, tour.id)
        localStorage.setItem(
          localStorageItemKeys.guidedTourInitialStepIndex,
          targetIndex.toString(),
        )
        navigate(destination, { state: { tourDirection: direction } })
      }
    }
  }

  return (
    <Joyride
      steps={tourSteps}
      tooltipComponent={renderTooltip}
      stepIndex={stepIndex}
      options={joyrideOptions}
      onEvent={handleCallback}
      continuous
      run={run}
    />
  )
}

function constructPayload({
  me,
  tour,
}: {
  me?: IUserInfoReadResponse
  tour: GuidedTourI
}) {
  const tours = me?.settings?.guidedTours || []
  const updatedTours = tours.filter((t) => t.id !== tour.id)
  return {
    settings: { guidedTours: [...updatedTours, tour] },
  }
}

// Locks the root element rather than <body>, whose overflow is toggled by the
// hamburger menu while the tour moves between pages.
function useLockPageScroll(isLocked: boolean) {
  useEffect(() => {
    if (!isLocked) return

    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'

    return () => {
      root.style.overflow = previousOverflow
    }
  }, [isLocked])
}

export function useIsStable(delay: number = 500) {
  const [isStable, setIsStable] = useState(false)
  const isFetching = useIsFetching()

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined

    if (isFetching === 0) {
      timeout = setTimeout(() => {
        setIsStable(true)
      }, delay)
    } else {
      setIsStable(false)
      if (timeout) clearTimeout(timeout)
    }

    return () => clearTimeout(timeout)
  }, [isFetching, delay])

  return isStable
}
